import { MenuOutlined } from '@ant-design/icons';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
	arrayMove,
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Empty, Table, Tooltip } from 'antd';
import ConfirmDelete from 'components/Global/confirmDeleteModel';
import Ellipse from 'components/Global/Ellipse';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { classAction } from 'redux/action/classAction';
import { customerOptionsAction } from 'redux/action/customerAction';
import { getEmployeeAction } from 'redux/action/employeeAction';
import { deleteApi } from 'redux/apis';
import { AppDispatch } from 'redux/store';
import {
	ActiveRule,
	CopyActionSvg,
	DeleteRuleActionSvg,
	EditRuleActionSvg,
	InactiveRule,
	QuickbooksEmployeeBlue,
} from 'utils/svgs';
import { checkPermission, getOptionLabel, toastText } from 'utils/utils';
import styles from './index.module.scss';

const CRITERIA_VISIBLE_LIMIT = 5;

// Criteria fields are 'ANY' (sentinel), '' (blank, customer/class only), a
// legacy single scalar value (pre-CAP-105 rules), or a multi-select array.
const formatCriteriaDisplay = (value: any, options: any[]): string => {
	if (value === 'ANY' || value === undefined || value === null) return 'Any';
	if (value === '') return 'Blank';

	const ids = Array.isArray(value) ? value : [value];
	if (ids.length === 0) return 'Any';

	const labels = ids.map((id: string) => getOptionLabel(id, options));
	if (labels.length > CRITERIA_VISIBLE_LIMIT) {
		return `${labels.slice(0, CRITERIA_VISIBLE_LIMIT).join(', ')} +${
			labels.length - CRITERIA_VISIBLE_LIMIT
		} more`;
	}
	return labels.join(', ');
};

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
	'data-row-key': string;
}

const Row = ({ children, ...props }: RowProps) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
		setActivatorNodeRef,
	} = useSortable({
		id: props['data-row-key'],
	});

	const style: React.CSSProperties = {
		...props.style,
		transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
		transition,
		...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
	};

	return (
		<tr {...props} ref={setNodeRef} style={style} {...attributes}>
			{React.Children.map(children, (child) => {
				if ((child as React.ReactElement).key === 'sort') {
					return React.cloneElement(child as React.ReactElement, {
						children: (
							<MenuOutlined
								ref={setActivatorNodeRef}
								style={{ touchAction: 'none', cursor: 'move' }}
								{...listeners}
							/>
						),
					});
				}
				return child;
			})}
		</tr>
	);
};

const DraggableTable = ({
	onChangePriority,
	getCustomRulesData,
	openAddRuleModal,
	tableName = 'splitData',
	tableLoading = 'tableLoading',
	searchValue,
	status,
}: any) => {
	const { Column } = Table;

	const dispatch = useDispatch<AppDispatch>();

	const data = useSelector((state: any) => state.customRules[tableName]);
	const isLoading = useSelector(
		(state: any) => state.customRules[tableLoading]
	);

	const { data: employeeList } = useSelector((state: any) => state.employees);
	const { data: classList } = useSelector((state: any) => state.class);
	const { optionData: customerList } = useSelector(
		(state: any) => state.customer
	);

	// Lists needed to resolve criteria ids to display names in the
	// expandable row below — fetched here so the criteria summary renders
	// correctly even if the Add/Edit Rule modal was never opened this visit.
	useEffect(() => {
		dispatch(getEmployeeAction());
		dispatch(classAction());
		dispatch(customerOptionsAction());
	}, []);

	const employeeOptions =
		employeeList?.map((employee: any) => ({
			value: employee?.id,
			title: employee?.fullName,
		})) || [];

	const classOptions =
		classList?.map((singleClass: any) => ({
			value: singleClass?.Id,
			title: singleClass?.FullyQualifiedName,
		})) || [];

	// Determine rule type and empty state message
	const getRuleTypeDisplayName = (tableName: string) => {
		switch (tableName) {
			case 'splitData':
				return 'Split Rules';
			case 'editData':
				return 'Edit Rules';
			case 'deleteData':
				return 'Delete Rules';
			default:
				return 'Time Rules';
		}
	};

	const ruleTypeDisplayName = getRuleTypeDisplayName(tableName);

	const hasActiveFilters = Boolean(
		searchValue?.trim() ||
		status
	);

	const CustomEmptyState = () => (
		<Empty
			image={Empty.PRESENTED_IMAGE_SIMPLE}
			description={
				isLoading
					? null
					: hasActiveFilters
						? `No ${ruleTypeDisplayName} match the selected filter criteria`
						: `No ${ruleTypeDisplayName} are currently available`
			}
			style={{
				padding: '40px 20px',
				margin: '20px 0'
			}}
		/>
	);

	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [deleteData, setDeleteData] = useState<any>(null);

	const selectedCompanyPermission = useSelector(
		(state: any) => state?.companies?.selectedCompanyDetails?.role?.permissions
	);

	// for check is there add permission
	const isAddRulePermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Custom Rules',
		permission: ['add'],
	});
	const isEditRulePermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Custom Rules',
		permission: ['edit'],
	});
	const isDeleteRulePermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Custom Rules',
		permission: ['delete'],
	});

	const onDragEnd = ({ active, over }: any) => {
		if (active.id !== over?.id) {
			const _data = data;
			const activeIndex = _data.findIndex((i: any) => i.id === active.id);
			const overIndex = _data.findIndex((i: any) => i.id === over?.id);
			const arr = arrayMove(_data, activeIndex, overIndex);
			const finalArr: any[] = [];
			arr.forEach((e: any, index) => {
				finalArr.push({
					...e,
					priority: index + 1,
				});
			});
			onChangePriority(finalArr);
		}
	};

	const deleteHandler = async () => {
		if (deleteData) {
			try {
				const response: any = await deleteApi(
					`/custom-rule/${deleteData.id}?companyId=${localStorage.getItem(
						'companyId'
					)}`
				);
				setIsDeleteModalOpen(false);
				toastText(response.data.message, 'success');
				getCustomRulesData();
			} catch (err: any) {
				let message = 'Something went wrong in deleting rule';
				if (err.response.data.message) {
					message = err.response.data.message;
				}
				setIsDeleteModalOpen(false);
				toastText(message, 'error');
			}
		}
	};

	const deleteModalOpen = (data: any) => {
		setIsDeleteModalOpen(true);
		setDeleteData(data);
	};

	return (
		<>
			<DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
				<SortableContext
					// rowKey array
					items={data.map((i: any) => i.id)}
					strategy={verticalListSortingStrategy}
				>
					<Table
						loading={isLoading}
						components={{
							body: {
								row: Row,
							},
						}}
						rowKey="id"
						scroll={{ y: '60vh' }}
						dataSource={data}
						pagination={false}
						locale={{ emptyText: <CustomEmptyState /> }}
						expandable={{
							expandedRowRender: (record: any) => (
								<div className={styles['draggable_table__criteria']}>
									<div>
										<strong>Employee:</strong>{' '}
										{formatCriteriaDisplay(
											record?.criteria?.employeeId,
											employeeOptions
										)}
									</div>
									<div>
										<strong>Customer:</strong>{' '}
										{formatCriteriaDisplay(
											record?.criteria?.customerId,
											customerList || []
										)}
									</div>
									<div>
										<strong>Class:</strong>{' '}
										{formatCriteriaDisplay(
											record?.criteria?.classId,
											classOptions
										)}
									</div>
								</div>
							),
						}}
					>
						<Column key="sort" className="bg-white" width="20%" />
						<Column
							// title="Priority"
							title={() => (
								<p
									style={{
										fontSize: '12px',
									}}
								>
									Priority
								</p>
							)}
							dataIndex="priority"
							key="priority"
							className="bg-white"
							width="20%"
							render={(value: number) => {
								return (
									<div className={styles['draggable_table__priority']}>
										<span>{value}</span>
									</div>
								);
							}}
						/>
						<Column
							// title="Rule Name"
							title={() => (
								<p
									style={{
										fontSize: '12px',
									}}
								>
									Rule Name
								</p>
							)}
							dataIndex="name"
							key="name"
							className="bg-white"
							width="20%"
							render={(value, data: any) => {
								return (
									<div className={styles['draggable_table__rule']}>
										<span
											className={styles['draggable_table__rule-employee']}
											onClick={() => {
												openAddRuleModal(data);
											}}
										>
											{value}
										</span>
										<QuickbooksEmployeeBlue />
									</div>
								);
							}}
						/>
						<Column
							// title="Description"
							title={() => (
								<p
									style={{
										fontSize: '12px',
									}}
								>
									Description
								</p>
							)}
							dataIndex="description"
							key="description"
							className="bg-white"
							width="20%"
							render={(value) => (
								<div className={styles['draggable_table__description']}>
									<Ellipse
										message={value}
										maxLength={300}
										isTooltip={true}
										tooltipMessage={value}
									/>
								</div>
							)}
						/>
						<Column
							// title="Status"
							title={() => (
								<p
									style={{
										fontSize: '12px',
									}}
								>
									Status
								</p>
							)}
							dataIndex="isActive"
							key="isActive"
							className="bg-white"
							width="20%"
							render={(value) => (
								<div className={styles['draggable_table__status']}>
									{value ? (
										<div className={styles['draggable_table__status-active']}>
											<ActiveRule />
											<span>Active</span>
										</div>
									) : (
										<div className={styles['draggable_table__status-inactive']}>
											<InactiveRule />
											<span>Inactive</span>
										</div>
									)}
								</div>
							)}
						/>
						{(isAddRulePermission ||
							isEditRulePermission ||
							isDeleteRulePermission) && (
								<Column
									// title="Actions"
									title={() => (
										<p
											style={{
												fontSize: '12px',
											}}
										>
											Actions
										</p>
									)}
									dataIndex="actions"
									key="actions"
									className="bg-white"
									width="20%"
									render={(value: string, data: any) => (
										<div className={styles['draggable_table__actions']}>
											{isAddRulePermission && (
												<Tooltip title="Copy Rule">
													<span
														className={styles['draggable_table__actions-copy']}
														onClick={() => {
															openAddRuleModal(data, true);
														}}
													>
														<CopyActionSvg />
													</span>
												</Tooltip>
											)}
											{isEditRulePermission && (
												<Tooltip title="Edit Rule">
													<span
														className={styles['draggable_table__actions-edit']}
														onClick={() => {
															openAddRuleModal(data);
														}}
													>
														<EditRuleActionSvg />
													</span>
												</Tooltip>
											)}
											{isDeleteRulePermission && (
												<Tooltip title="Delete Rule">
													<span
														className={styles['draggable_table__actions-delete']}
														onClick={() => deleteModalOpen(data)}
													>
														<DeleteRuleActionSvg />
													</span>
												</Tooltip>
											)}
										</div>
									)}
								/>
							)}
					</Table>
				</SortableContext>
			</DndContext>
			{isDeleteModalOpen && (
				<ConfirmDelete
					handleCancel={() => {
						setIsDeleteModalOpen(false);
						setDeleteData(null);
					}}
					handleOk={() => {
						setIsDeleteModalOpen(false);
						setDeleteData(null);
					}}
					isModalOpen={isDeleteModalOpen}
					deleteHandler={deleteHandler}
				/>
			)}
		</>
	);
};

export default DraggableTable;
